/**
 * useMeterRenderer — thin orchestration shell for the canvas meter.
 *
 * The shell composes four sibling workshops (all under `./meter/`):
 *
 * - `meter/meterGeometry.ts` — DPR + ResizeObserver + recompute (pure)
 * - `meter/useMeterTokenColors.ts` — setup-time token-colour refs
 * - `meter/paintMeter.ts` — per-frame draw + idle oscillator (pure)
 * - `meter/meterCompletion.ts` — completion + abort animations
 * - `meter/meterMilestone.ts` — per-phase bloom controller (pure factory)
 * - this file — rAF loop + watchers + onMount/onUnmount lifecycle
 *
 * The loop runs continuously: at idle the dial breathes (±2° over 6s),
 * rings paint as phantom tracks, the machined center cap persists
 * across phases. Active visuals gate on a single boolean.
 */

import { watch, type Ref } from "vue";
import { SmoothProgress } from "@mkbabb/keyframes.js";
import {
    DAMPING,
    SNAP_THRESHOLD,
    useRAFLoop,
} from "@mkbabb/glass-ui/motion-core";
import type { RAFLoopTiming } from "@mkbabb/glass-ui/motion-core";
import {
    metricProgress,
    type MetricKey,
} from "../ui/meter/canvas/meter/config.ts";
import { usePhaseTransition } from "./usePhaseTransition.ts";
import { createMeterMilestone } from "./meter/meterMilestone.ts";
import { createRingPulse } from "../ui/meter/canvas/meter/ringPulse.ts";
import type { RingPulse } from "../ui/meter/canvas/meter/ringPulse.ts";
import { useRingPulseClock } from "./meter/useRingPulseClock.ts";
import { handoffRemap, HANDOFF_RETREAT_FRACTION } from "./meter/math.ts";
import type { MeterHandoff } from "./meter/paintMeter.ts";
import {
    recomputeGeometry,
    watchMeterResize,
    type MeterGeometryBag,
} from "./meter/meterGeometry.ts";
import { useMeterTokenColors } from "./meter/useMeterTokenColors.ts";
import { drawFrame as paintFrame } from "./meter/paintMeter.ts";
import {
    paintCompleteHold,
    watchMeterEngine,
    type MeterCompletionState,
} from "./meter/meterCompletion.ts";
import type { LiveTestData } from "@features/speedtest/state/index.ts";
import { useChoreographyStore } from "@src/stores/useChoreographyStore.ts";

/**
 * Fraction of the phase-handoff clock the needle spends RETREATING before
 * it advances toward the new phase's target — the first beat of the
 * three-beat handoff shape (retreat → cross-fade → arrive). Re-exported from
 * the shared `meter/math` shaping module so the magic `1/3` is ONE declared
 * value (AV.W11b unified the renderer's `RETREAT` and `MeterColumn.vue`'s
 * `DRAIN` onto a single source via `handoffRemap`). Kept as the named
 * `RETREAT` export for the renderer's own readability + any external readers.
 */
export const RETREAT = HANDOFF_RETREAT_FRACTION;

export interface MeterRendererProps {
    speedtestData: LiveTestData | null;
    currentEnginePhase: string | undefined;
    isRunning: boolean;
    getSpeedtestStateAmount: (stateName: string, kind?: string) => number;
}

export function useMeterRenderer(
    meterRef: Ref<HTMLCanvasElement | null>,
    props: MeterRendererProps,
) {
    // Geometry bag — owned by the shell, mutated by the
    // `meterGeometry` pure-function workshop on `recompute()`.
    const bag: MeterGeometryBag = {
        canvas: null,
        config: null,
        rings: null,
        dial: null,
    };
    // Completion state — shared with `meterCompletion`'s play
    // functions (the awaits mutate `isPlayingCompletion`/`isCompleted`
    // so the rAF tick can gate cleanly). `lastStateName` has a single
    // writer — the rAF `tick()` below records each measured phase as it
    // paints it; the completion lane only reads it, and the abort /
    // run-start paths clear it.
    const state: MeterCompletionState = {
        isPlayingCompletion: false,
        isCompleted: false,
        lastStateName: undefined,
    };
    let resizeObserverInstalled = false;

    const smooth = new SmoothProgress({
        damping: DAMPING.canvasNeedle,
        snapThreshold: SNAP_THRESHOLD.canvas,
    });

    // W5-L1 — the shared phase-handoff orchestrator. The renderer is one
    // channel of the unified ≈600ms boundary event: it samples the
    // `ManualTimeline` clock here instead of running its own boundary
    // heuristic. `retreatFrom` captures the needle's fill at the instant
    // a handoff begins, so the needle eases back from exactly where it
    // was — the connective "rewind to measure again" gesture (A4 §3).
    const handoff = usePhaseTransition();
    let retreatFrom = 0;
    let lastHandoffActive = false;

    // W5-C4 — the per-phase milestone bloom. Owned as a plain object;
    // the paint loop drives its gate (`observe`) and samples its
    // envelope (`intensity`). The bloom's own choreography rAF lives
    // inside keyframes.js (precept 10).
    const milestone = createMeterMilestone();

    // AV.W14.1 — the at-rest ring-pulse. The IDLE-time complement of the
    // milestone bloom: where the milestone fires per-phase on the running
    // needle settling, the pulse fires at IDLE on the jittered clock arm so
    // the parked instrument reads as alive. Constructed against the LIVE
    // bag once geometry is valid; rebuilt on a geometry rebuild (resize /
    // DPR). The paint loop only SAMPLES its envelope — its choreography rAF
    // lives inside keyframes.js (precept 10), demand-armed (no resident
    // loop). DDR-AV-W14-B0-PULSE + DDR-AV-W14-BREATH.
    let ringPulse: RingPulse | null = null;

    // Token-color refs cascade theme / tier swaps without a remount;
    // `recompute()` calls `tokens.{track,dial}.refresh()` so post-mount
    // cascade mutations re-resolve before geometry rebuilds.
    const tokens = useMeterTokenColors();

    function recompute(): boolean {
        const ok = recomputeGeometry(meterRef, bag, tokens);
        // AV.W14.1 — (re)bind the ring-pulse to the freshly-built canvas +
        // rings. A geometry rebuild (resize / DPR) swaps `bag.canvas`/
        // `bag.rings`, so the pulse is reconstructed against the live pair;
        // the pulse's per-frame paint reads the LIVE bag, so an in-flight
        // pulse simply continues against the new geometry on the next arm.
        if (ok && bag.canvas && bag.rings) {
            ringPulse?.stop();
            ringPulse = createRingPulse(bag.canvas, bag.rings);
        }
        return ok;
    }

    function drawFrame(
        stateName: string | undefined,
        t: number,
        time: number,
        meterHandoff?: MeterHandoff,
        milestoneIntensity = 0,
    ): void {
        paintFrame(bag, stateName, t, time, meterHandoff, milestoneIntensity);
        // AV.W14.1 — the ring-pulse paints AFTER the active/idle arcs +
        // dial. A no-op at intensity 0 (only the ≤380ms arm window pays the
        // cost). It fires at IDLE (`stateName` undefined → neutral ring-0
        // hue) and is harmless during a run (the clock holds no arms while
        // running, so `intensity` stays 0 mid-run).
        if (ringPulse && bag.canvas && bag.rings && ringPulse.intensity() > 0) {
            ringPulse.drawPulse(bag.canvas, bag.rings, stateName);
        }
    }

    /**
     * Post-resize recovery paint. When the meter cell remounts from a
     * collapsed-state path (e.g. `.meter-col` was stale-`data-complete`
     * and just lifted), the observer fires and we re-paint into
     * whatever state the engine is in so the idle/active/complete
     * surface reads correctly without waiting for the next rAF tick.
     */
    function recoveryPaint(): void {
        if (state.isCompleted && state.lastStateName) {
            drawFrame(state.lastStateName, 1, performance.now());
        } else if (props.isRunning) {
            drawFrame(undefined, 0, performance.now());
            startLoop();
        } else if (!props.speedtestData) {
            drawFrame(undefined, 0, performance.now());
            startLoop();
        }
    }

    function installResizeObserver(): void {
        if (resizeObserverInstalled) return;
        watchMeterResize(meterRef, () => {
            if (recompute()) recoveryPaint();
        });
        resizeObserverInstalled = true;
    }

    function initialize(): void {
        if (!meterRef.value) return;
        const ok = recompute();
        // Install the observer unconditionally so the back-nav
        // zero-rect path can recover once `.meter-col` lifts.
        installResizeObserver();
        if (!ok) return;

        drawFrame(undefined, 0, performance.now());

        if (props.isRunning) {
            startLoop();
        } else if (props.speedtestData) {
            // Re-mount onto already-complete state (router-back, HMR):
            // paint the rainbow-rings + needle-at-download hold.
            const name = props.currentEnginePhase;
            state.lastStateName =
                name === "ping" || name === "download" || name === "upload"
                    ? name
                    : "upload";
            state.isCompleted = true;
            paintCompleteHold(bag, props.getSpeedtestStateAmount);
        } else {
            // Spin the always-on loop so the breathing oscillator stays alive.
            startLoop();
        }
    }

    // ── rAF render loop ───────────────────────────────────────────────

    // T5 / AUA-F7-STALE-T5 — the idle breath loop must PARK (zero rAF
    // churn) under `prefers-reduced-motion` and while the document is
    // hidden. `useRAFLoop`'s `respectReducedMotion` + `pauseWhenHidden`
    // both DEFAULT true, so the prior bare `{ immediate: false }` already
    // inherited the gate — but inheriting it left the parking contract
    // implicit at this call site. They are now DECLARED explicitly so the
    // meter's reduced-motion / hidden-tab parking is legible here, not a
    // silent library default. Under PRM `start()` schedules ZERO frames
    // (the lib's run-predicate fails), so the loop never spins; the one
    // static idle frame is painted by `parkIdleFrame` below. Off-SCREEN
    // parking (scroll-out, distinct from tab-hidden) is driven by
    // `MeterColumn.vue`'s `contentvisibilityautostatechange` listener
    // calling `stopLoop`/`startLoop` — `pauseWhenHidden` covers
    // `document.hidden` only, not `content-visibility: hidden`.
    const renderLoop = useRAFLoop(tick, {
        immediate: false,
        respectReducedMotion: true,
        pauseWhenHidden: true,
    });

    /**
     * Paint a single static idle frame at the parked idle angle. Under
     * `prefers-reduced-motion` the loop delivers no frames, so the meter
     * would otherwise hold whatever was last painted (or nothing); this
     * stamps the canonical idle frame once so the dial reads as a parked
     * instrument, not a frozen mid-sweep. `idleSweep` already returns the
     * bare `startAngle` when reduced, so the frame is the true idle rest.
     */
    function parkIdleFrame(): void {
        if (!bag.config) return;
        drawFrame(undefined, 0, performance.now());
    }

    function startLoop(): void {
        stopLoop();
        renderLoop.start();
        // Under PRM `start()` scheduled nothing (zero rAF), so paint the
        // one parked idle frame here — covers a PRM toggle that occurs
        // while the meter is already mounted, not just the mount path.
        if (renderLoop.isReducedMotion.value) parkIdleFrame();
    }

    function stopLoop(): void {
        renderLoop.stop();
    }

    /**
     * The rAF loop seam. `useRAFLoop` schedules this once per frame with
     * `{ delta, now }`; the body picks the paint:
     *
     * - completion / abort plays gate the loop off via
     *   `isPlayingCompletion` / `isCompleted` — those paths own the canvas.
     * - no `speedtestData` → idle frame (breathing oscillator).
     * - a live engine phase → active frame, needle chasing the
     *   `SmoothProgress` target.
     *
     * The `lastStateName` fallback only fills a *mid-run* inter-phase gap:
     * it substitutes a phase the engine already reported THIS run when
     * `currentEnginePhase` is momentarily `undefined` between phases. At
     * run start `lastStateName` is `undefined` (the `isRunning` rising
     * edge in `meterCompletion` clears it), so the idle frame paints
     * until the real opening phase arrives — never the prior run's colour.
     *
     * W5-L1 — at a phase boundary the renderer is ONE channel of the
     * unified `usePhaseTransition` handoff, not an independent actor. The
     * old `smooth.reset(current * 0.5)` half-reset heuristic is retired.
     * While the shared `ManualTimeline` runs, the needle plays an
     * explicit retreat-then-advance shaped by the handoff clock: the
     * first third eases the fill back toward 0 (the instrument "rewinds
     * to measure again"), the rest advances toward the new phase's live
     * target. The smoother is seeded at the handoff's end so the
     * steady-state chase resumes seamlessly.
     */
    function tick({ delta, now }: RAFLoopTiming): void {
        if (state.isPlayingCompletion || state.isCompleted) return;
        if (!bag.config) return;

        if (!props.speedtestData) {
            drawFrame(undefined, 0, now);
            return;
        }

        let name = props.currentEnginePhase;
        if (name !== "ping" && name !== "download" && name !== "upload") {
            // Mid-run inter-phase gap only: hold the last reported phase
            // so the dial doesn't flicker to idle between phases. Pre-ping
            // (run start) `lastStateName` is undefined → idle frame below.
            name = state.lastStateName;
        }
        if (!name) {
            drawFrame(undefined, 0, now);
            return;
        }
        state.lastStateName = name;

        // Per-metric peak (linear ms / log10 Mbps) — see meter/config.ts.
        // Jitter never drives the live needle (constrained upstream).
        const targetT = metricProgress(
            name as MetricKey,
            props.getSpeedtestStateAmount(name),
        );

        if (handoff.active.value) {
            // Rising edge of a handoff — capture the needle's current
            // fill as the retreat origin (once per handoff).
            if (!lastHandoffActive) {
                retreatFrom = smooth.current;
                lastHandoffActive = true;
            }
            const p = handoff.progress.value;
            // The needle shape depends on whether the boundary has a
            // phase being LEFT. An interior boundary (`ping ↔ download ↔
            // upload`) plays the three-beat retreat-then-advance via the
            // shared `handoffRemap` shape: retreat `retreatFrom → 0` over
            // the first `RETREAT` of the clock (the instrument "rewinds to
            // measure again"), then advance `0 → targetT` over the rest.
            // This is the SAME two-segment shape `MeterColumn.vue`'s under-
            // meter bar plays as its DRAIN (AV.W11b unified the dup — one
            // helper, one split fraction). The opening ARRIVAL edge
            // (`undefined → ping`) has no prior phase to retreat from — the
            // needle simply advances `0 → targetT` across the whole clock,
            // gliding in alongside the ring + `--phase-color` cross-fade
            // (AG-A4-EDGE-ASYMMETRY). Both legs are linear in the already-
            // eased clock — `easeInOutCubic` lives in the ManualTimeline.
            const hasRetreat = handoff.fromPhase.value != null;
            const needleT = hasRetreat
                ? handoffRemap(p, retreatFrom, targetT)
                : targetT * p;
            // Keep the smoother converged on the handoff value so the
            // post-handoff steady-state chase picks up without a jump.
            smooth.reset(needleT);
            drawFrame(name, needleT, now, {
                fromPhase: handoff.fromPhase.value,
                toPhase: handoff.toPhase.value,
                blend: p,
            });
            return;
        }
        lastHandoffActive = false;

        smooth.setTarget(targetT);
        const needleT = smooth.tickDt(delta);
        // W5-C4 — gate the milestone bloom on the steady-state needle.
        // `smooth.settled` flips true once the needle converges on the
        // phase's measured value; the milestone fires its one-shot bloom
        // on that first settled frame, then the gate holds until the
        // next phase re-arms it.
        milestone.observe(name, smooth.settled);
        drawFrame(name, needleT, now, undefined, milestone.intensity());
    }

    // ── Completion + abort wiring ────────────────────────────────────
    // Play paths + engine watchers live in `meter/meterCompletion.ts`; the
    // deps handle bundles the closure state so the awaits can mutate
    // the rAF gate slots.
    //
    // AJ-W1-θ Path θ-A.1.a: the `choreographySignal` deps slot lets the
    // meter renderer arm + disarm the chassis cascade gate. The signal
    // routes through the `useChoreographyStore` singleton so the
    // `useEngineState` consumer (used by SpeedtestResults' chassis attrs)
    // sees the same source of truth, regardless of mount order.
    const choreography = useChoreographyStore();
    watchMeterEngine(props, {
        bag,
        state,
        smooth,
        getAmount: props.getSpeedtestStateAmount,
        stopLoop,
        startLoop,
        choreographySignal: {
            reset: choreography.resetChoreography,
            markComplete: choreography.markChoreographyComplete,
        },
        // AV.W14.3 — the idle-on-STOP settle-back acknowledgement. After the
        // abort settle lands the needle at the hero rest pose, fire ONE
        // at-rest ring-pulse so post-STOP idle carries a fresh bloom that a
        // cold idle does not (the SAME vocabulary, no new mechanism). The
        // pulse's own `respectReducedMotion` parks it under PRM.
        settleBack: () => {
            void ringPulse?.play();
        },
    });

    // W5-C4 — clear the milestone gate on the `isRunning` rising edge so
    // a fresh run (including a re-test of the same opening phase) earns
    // its blooms anew, rather than inheriting the prior run's fired set.
    watch(
        () => props.isRunning,
        (running) => {
            if (running) milestone.reset();
        },
    );

    // AV.W14.2 — the ONE jittered clock-owner. It arms the at-rest pulse on
    // a non-metronomic schedule (~20s, then 25s + jitter), parks under PRM,
    // and resets on the `isRunning` rising edge (no ramp-up pulse). BOTH the
    // dial (here) and the dock signature (`Dock.vue`, B5) consume its
    // `armNonce` — ONE clock, two channels (feedback_clock_owner_pattern).
    const pulseClock = useRingPulseClock();
    watch(pulseClock.armNonce, () => {
        // Arm a pulse only at idle with a valid instrument. The clock
        // already withholds arms while running; this is the renderer-side
        // guard so a stray arm during a play (completion/abort owns the
        // canvas) is a no-op rather than a paint over the whirl.
        if (
            ringPulse &&
            !state.isPlayingCompletion &&
            !state.isCompleted &&
            !props.isRunning
        ) {
            void ringPulse.play();
        }
    });

    function dispose(): void {
        renderLoop.dispose();
        // AV.W14.1 — cancel any in-flight pulse so its managed rAF does not
        // outlive the renderer (the clock's own timer self-clears via
        // `onScopeDispose`).
        ringPulse?.stop();
    }

    /**
     * Off-screen park gate (T5 / AUA-F7 — the second half of the parking
     * story). `MeterColumn.vue`'s `contentvisibilityautostatechange`
     * listener calls this with `e.skipped`: `true` when `.meter-col`
     * scrolls out of the `content-visibility: auto` skip region, `false`
     * on re-entry. The lib's `pauseWhenHidden` only covers a hidden
     * DOCUMENT (tab switch); scroll-out is a distinct signal the lib
     * does not gate, so the host drives it here.
     *
     * On skip: stop the loop (zero rAF while off-screen). On re-entry:
     * resume the loop ONLY if the renderer is in a loop-driven state —
     * never restart it over a completion/abort play (those own the
     * canvas via the `isPlayingCompletion`/`isCompleted` gate) or over an
     * already-complete hold. This mirrors `recoveryPaint`'s gate so the
     * surface re-reads correctly without a custom scheduler or an
     * IntersectionObserver fallback (inv-16).
     */
    function setOffscreen(skipped: boolean): void {
        if (skipped) {
            stopLoop();
            return;
        }
        if (state.isPlayingCompletion || state.isCompleted) return;
        startLoop();
    }

    return { initialize, dispose, setOffscreen };
}
